import * as THREE from "three";
import type { AnimRig } from "../rig";
import { RING } from "./blobShadow";
import type { BarState } from "./hpBars";
import type { Ballistic, UnitLook } from "../unitFx";
import type { EnemyStatusMarks } from "../../../../../art/vfx.js";
import type { BossState } from "./bossStage";
import type { EnemyLook, EnemyStatus } from "./enemyLook";
import type { Ghost } from "./enemyGhost";
import type { GroundTrack } from "./groundFollow";
import type { FootIk } from "./footIk";

/** One enemy's pooled view (enemies.ts EnemyViews) and the tuning its feel, marks and death read. */

/**
 * How a dying view leaves: the baked topple, a blast launch, the flyer's spiral fall — or, for the Starfall Wyrm (M40,
 * wyrmView.ts), its own long `die` fall, and its `flee` up and out of sight (no death at all: it got away).
 */
export const enum Death { Topple, Launch, Spiral, Fall, Flee }

export interface EnemyView {
  root: THREE.Group; body: THREE.Object3D; rig: AnimRig; hq: boolean; model: string; archId: string; boss: boolean; air: boolean; key: string;
  look: UnitLook | null;
  /** The model's `.body` node (the clip's sink moves it) and the bomber's bomb, when the model has them. */
  torso: THREE.Object3D | null; bomb: THREE.Object3D | null;
  /** Per-archetype scale (units F2), the model's own height, and what they give: ring width and bar height. */
  scale: number; height: number; ringWidth: number; barY: number;
  /**
   * The body's height at scale 1, what the boss stage and death bursts size by: a boss model's head top (bossSize.ts headTop:
   * its banner, stacks or staff are not its body), else `height`.
   */
  bodyH: number;
  bar: BarState; lastHp: number; maxHp: number;
  prev: THREE.Vector3; cur: THREE.Vector3; phase: number; speed: number;
  /**
   * The ground one cycle of its `walk` covers as drawn (enemyGait.ts, from the art's measured stride; 0 for a model
   * without one), where the walk is (cycles walked: the clip follows the distance covered, so planted feet stay put),
   * the root's ground position last frame (x, z) and how much it leans into a run this frame (0–1: the cadence cap
   * leaves its feet behind).
   */
  stride: number; walked: number; lastX: number; lastZ: number; run: number;
  /** Whether this turn has locked its feet first (enemyTurn.ts turnBody). */
  turnHeld: boolean;
  /** Its rounded path (enemyTurn.ts roundPath): where it is drawn, its heading and turn rate, the sim's last position. */
  path: { x: number; z: number; head: number; rate: number; sx: number; sz: number; set: boolean };
  /** Its walk is keyed too sparsely to hold a sole between keys (enemyGait.ts coarseWalk): its planted feet are locked. */
  coarse: boolean;
  /** Seconds since a one-shot last played on it (the foot lock holds through the fade back to the walk). */
  sinceShot: number;
  /** Knockback offset along (kdx, kdz) on a spring, the hit stretch timer and the spawn-pop clock. */
  knock: { x: number; v: number }; kdx: number; kdz: number; stretch: number; age: number;
  /** Death: seconds since, how, when the dissolve starts and how long it runs, and the launch state. */
  dead: number; death: Death; dissolveAt: number; dissolveFor: number; fly: Ballistic; spinAxis: THREE.Vector3; spinRate: number; fallFrom: number;
  /** The boss moment's state (bossStage.ts); null for grunts. */
  bs: BossState | null;
  /**
   * Type readability (enemyLook.ts, enemyMarks.ts): the role look, its colour, the aura radius, the plate's height over
   * the feet (`headY` at rest; `headNow` this frame, with the climb out of the portal and a boss's staged growth and
   * rise), the HP shown on the plate (−1 hides it) and the two status icons (atlas cells, −1 for none).
   */
  role: EnemyLook; colour: THREE.Color; aura: number; headY: number; headNow: number;
  /** Its own `headY`, which `headY` returns to on a new spawn (a body drawn in its place moves the plate meanwhile). */
  headRest: number; hp: number; s1: number; s2: number;
  /**
   * Status marks drawn on the unit (frost crust, a flame per burn stack, a green pip per shred stack; statusFx.ts),
   * gated so a status shows when the round that brings it lands; `waits` are when each status began waiting (gate time).
   */
  marks: EnemyStatusMarks; waits: number[];
  /**
   * Hits waiting for their round to land (statusFx.ts ImpactGate): `owed` is the damage the sim has dealt that the
   * bar does not show yet, `simHp` the sim's health after the last tick and `runHp` its running value through this
   * tick's hits (a killing blow owes only what was left). `doomed`: killed by a round still in the air — the body
   * walks on along the road at its last pace (`ghost`, enemyGhost.ts), its plate at the health it had, until the round
   * lands and it dies.
   */
  owed: number; simHp: number; runHp: number; doomed: boolean; ghost: Ghost;
  /** A Burrower's dive (ENEMIES_V2): 0 on the surface, 1 underground — the body sinks and its ring fades (enemiesV2Fx.ts eases it). */
  buried: number;
  /** M36: the rare's chapter (1–6) when this view is the rare spawn (rareShiny.ts shines it), else 0. Fixed per pool key. */
  rare: number;
  /** M36: the immunity mask the sim gives it (rare/rareApi.ts), read each tick with its statuses. */
  imm: number;
  /** M40: this view is the Starfall Wyrm (wyrmView.ts: its arrival dive, roars, flee and fall). Fixed per pool key. */
  wyrm: boolean;
  /** Its soles on the ground as drawn: the smoothed height under its footprint and its tilt with the slope (groundFollow.ts). */
  foot: GroundTrack;
  /** Its soles placed on uneven ground after the clip (footIk.ts); null for a rig without legs, or a flyer. */
  ik: FootIk | null;
}

/** Death timings (units F4): the dissolve takes 0.45 s once the topple has landed, the bar and ring fade over 0.3 s. */
export const DIE = { dissolve: 0.45, toppleStart: 0.55, launchStart: 0.45, spiralFall: 0.6, spiralYaw: 3, sinkFloor: -0.03, ringFade: 0.3 } as const;
/** Movement feel (units F5). */
export const FEEL = { knock: 0.05, knockMax: 0.08, springOmega: 22, stretch: -0.12, stretchFor: 0.06, lean: 0.25, leanAtSpeed: 2, hitClipFraction: 0.08 } as const;
/** Blast launch: which towers' impacts throw a body, and how near the impact it must die (cells). */
export const BLAST_TOWERS = new Set(["cannon", "mortar", "fire"]);
export const BLAST_RADIUS = 0.6;

export const RING_ENEMY = new THREE.Color(RING.enemy);
export const RING_ICE = new THREE.Color(0xbfe8ff);
export const RING_POISON = new THREE.Color(0x7cff5a);
export const RING_BURN = new THREE.Color(0xff8a2a);
export const RING_BOOST = new THREE.Color(0xffcc4a);


/** A new unit climbs out of the portal over this long (s): scale and height ease in, its marks fade in. */
export const EMERGE_SECONDS = 0.55;
/** ...starting this far below its path, at this fraction of its size. */
export const EMERGE_DEPTH = 0.45;
export const EMERGE_SCALE = 0.2;
/**
 * Badge radius in CSS px by kind of unit. Kept small (was 8.5 / 10 / 12): at phone zoom a unit is ~25 px tall, and a
 * column of 17 px badges hid the column of units under it. The glyph's shape still reads at 14 px.
 */
export const BADGE_PX = { unit: 7, big: 8.5, boss: 10 } as const;
/** Ground ring diameter (world units) by kind of unit (the boss keeps its rune ring: UnitRings). */
export const MARK_RING = { unit: 0.5, big: 0.68, boss: 1.05 } as const;
/** Auras (heal reach, shield wall) are a hint, not a unit: kept faint. */
export const AURA_ALPHA = 0.42;
/**
 * The plate's anchor sits this far above the model's top (world units); the plate itself then stands a fixed few
 * pixels above the anchor (enemyMarksShader.ts, PLATE_GAP_PX), so it never covers the body at any zoom.
 */
export const PLATE_LIFT = 0.04;
/** Floating numbers: kill points in gold, fortress losses in red (linear colours), sizes in CSS px. */
export const FLOAT = { gold: new THREE.Color(0xffd84a), red: new THREE.Color(0xff4a3a), killPx: 15, leakPx: 22, life: 0.95, baseLift: 1.3 } as const;
/**
 * Status priority when more than two apply: what changes the player's next decision first. Only states that come and
 * go are icons. Plain armour is a trait of the type, not a state: shown on every armoured unit (every raider), it
 * stacked a second badge beside every type badge in the wave and said nothing the type badge does not.
 */
export const STATUS_ORDER: readonly EnemyStatus[] = ["burrowed", "casting", "burning", "slowed", "shielded", "shred", "boosted"];

/** The climb out of the portal (0 → 1 with a small overshoot) for a unit `age` seconds old. */
export function emergeScale(age: number): number {
  const t = Math.min(1, Math.max(0, age / EMERGE_SECONDS));
  return EMERGE_SCALE + (1 - EMERGE_SCALE) * (1 + 2.2 * Math.pow(t - 1, 3) + 1.2 * Math.pow(t - 1, 2));
}
